import { useTranslations } from 'next-intl';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BaseLineCut } from '@/app/components/icons/BaseLineCut';
import { ImageUploaderWithPreview } from '@/app/components/shared/ImageUploaderWithPreview';
import { TemplateImgCropModal } from '@/app/components/shared/ImgCropModals/TemplateImgCropModal';
import type { CropOption } from '@/app/components/shared/ImgCropModals/use-crop-options';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { useImageSelectModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { FormImagePrompt, ImagePrompt, ImageUploadFile, ImageUploadFileInfo } from '@/app/types';
import { checkSourceImageSize } from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import { useRouter } from '@/i18n/routing';
import { useImageSlots } from '../../../create/_hooks/use-images-slots';
import { useTemplatesPanel } from '../../PanelContext';
import type { ImageSample } from '../types';
import { ensureImageSize } from '../utils';
import { SampleSelectHoverCard } from './SampleSelectHoverCard';

// 将裁剪比例转换为数值（数据已在入口处验证过格式）
const parseAspectRatio = (label: string): number | 'free' => {
  if (label === 'free') return 'free';
  const [width, height] = label.split(':').map(Number);
  return width / height;
};

interface ImageUploadSectionProps {
  imageUploadCount: number;
  imageUploadHints?: string[];
  imagePrompts: (FormImagePrompt | null)[];
  maxImageSize: number;
  processingImageIdx: number | null;
  uploadingImages: number[];
  handleChangeFiles: (
    idx: number,
    uploadFile: ImageUploadFile,
    uploadFileInfo?: ImageUploadFileInfo,
  ) => Promise<FormImagePrompt | null | undefined>;
  updateImagePrompt: (idx: number, imagePrompt: FormImagePrompt | null) => void;
  forceCropRatio: string;
  activeCropRatios: string[];
  defaultCropIndex: number;
  imageSamples?: ImageSample[];
  templateId?: string;
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
}

export const ImageUploadSection = forwardRef<HTMLDivElement, ImageUploadSectionProps>(
  (
    {
      imageUploadCount,
      imageUploadHints,
      imagePrompts,
      maxImageSize,
      processingImageIdx,
      uploadingImages,
      handleChangeFiles,
      updateImagePrompt,
      forceCropRatio,
      activeCropRatios,
      defaultCropIndex,
      imageSamples,
      templateId,
      allowTempMode = false,
    },
    ref,
  ) => {
    const t = useTranslations();
    const [forceCropIndex, setForceCropIndex] = useState<number | null>(null);
    const [forceCropOriginalData, setForceCropOriginalData] = useState<FormImagePrompt | null>(null);
    const forceCropReasonRef = useRef<'upload' | 'templateChange' | null>(null);
    const router = useRouter();

    const { flyEndId } = useStore((state) => ({
      flyEndId: state.flyEndId,
    }));

    const { isTemplateDetailDrawerOpen } = useTemplatesPanel();

    // 复用 useImageSlots，实现多图选择、自动分配槽位和拖拽交换
    const {
      emptySlotsCount,
      handleChangeFiles: baseHandleChangeFiles,
      handleSwapImages,
    } = useImageSlots({
      slotCounts: imageUploadCount,
      formType: 'scene',
      updateImagePrompt,
    });

    // 检查图片（或选区）是否符合强制裁剪比例要求
    const checkImageAspectRatio = (image: ImagePrompt): boolean => {
      if (!checkSourceImageSize(image)) return false;
      if (forceCropRatio === 'free') return true; // 自由比例不限制
      const target = parseAspectRatio(forceCropRatio);
      if (target === 'free') return true;

      // 优先检查用户选区比例，其次使用处理后尺寸，最后使用原图尺寸
      const hasSelection = Array.isArray(image.selectedImageRange) && image.selectedImageRange.length === 4;
      let w: number | undefined;
      let h: number | undefined;

      if (hasSelection) {
        const [x1, y1, x2, y2] = image.selectedImageRange as [number, number, number, number];
        w = Math.max(1, Math.round(x2 - x1));
        h = Math.max(1, Math.round(y2 - y1));
      } else if (image.selectedImageWidth && image.selectedImageHeight) {
        w = image.selectedImageWidth;
        h = image.selectedImageHeight;
      } else if (image.sourceImageWidth && image.sourceImageHeight) {
        w = image.sourceImageWidth;
        h = image.sourceImageHeight;
      }

      if (!w || !h) return false;
      const ratio = w / h;
      return Math.abs(ratio - target) <= 1e-6;
    };

    const canCrop = useMemo(() => {
      return Boolean(forceCropRatio || activeCropRatios.length > 0);
    }, [forceCropRatio, activeCropRatios]);

    /** 全局拖拽上传时从第一个空槽开始，与 useImageSlots 的 findAvailableSlot(0) 一致 */
    const firstEmptySlotIndex = useMemo(() => {
      for (let i = 0; i < imageUploadCount; i++) {
        if (!imagePrompts[i]) return i;
      }
      return -1;
    }, [imagePrompts, imageUploadCount]);

    const cropOptions: CropOption[] = useMemo(() => {
      if (forceCropRatio) return [{ label: forceCropRatio, value: parseAspectRatio(forceCropRatio) }];
      return activeCropRatios.map((ratio) => ({ label: ratio, value: parseAspectRatio(ratio) }));
    }, [forceCropRatio, activeCropRatios]);

    //用于更新裁剪图片
    const updateSelectImage = (idx: number, newPrompt: ImagePrompt) => {
      // 如果是强制裁剪，使用保存的原始上传数据
      if (forceCropIndex === idx && forceCropOriginalData) {
        updateImagePrompt(idx, {
          ...forceCropOriginalData, // 使用原始上传的ID、status等信息
          prompt: newPrompt, // 只更新裁剪后的图片数据
        });
        setForceCropIndex(null); // 清除强制裁剪标记
        setForceCropOriginalData(null); // 清除原始数据
      } else {
        // 普通的图片选择更新
        const oldImagePrompt = imagePrompts[idx];
        if (!oldImagePrompt) return;
        updateImagePrompt(idx, {
          ...oldImagePrompt,
          prompt: newPrompt,
        });
      }
    };

    const { selectModalImageSource, handleCloseSelectModal, handleSaveSelectImage, handleOpenSelectModal } =
      useImageSelectModal({
        updateSelectImage,
        maxImageSize,
        // 与选图时的 allowTempMode 保持一致；TemplateForm 侧已挂 useTemplateTempImagesUpload 负责补传
        allowTempMode,
      });

    // 在切换模版或强制比例变化时检查现有图片
    useEffect(() => {
      const checkAndOpenNextInvalid = async () => {
        for (let i = 0; i < imageUploadCount; i++) {
          const formPrompt = imagePrompts[i];
          const img = formPrompt?.prompt;
          if (!img) continue;
          const hasSize = await ensureImageSize(img);
          if (!hasSize) continue;
          if (!checkImageAspectRatio(img)) {
            setForceCropIndex(i);
            setForceCropOriginalData(formPrompt);
            forceCropReasonRef.current = 'templateChange';
            handleOpenSelectModal(i, img);
            return;
          }
        }
      };

      const needForce = Boolean(forceCropRatio && forceCropRatio !== 'free');
      if (!needForce) return;
      if (!templateId) return; // 依赖模板ID作为变化信号
      if (selectModalImageSource) return; // 避免重复弹窗
      checkAndOpenNextInvalid();
    }, [templateId, forceCropRatio]);

    const internalHandleChangeFiles = async (
      idx: number,
      uploadFile: ImageUploadFile,
      uploadFileInfo?: ImageUploadFileInfo,
    ) => {
      let resolvedTargetIdx = idx;
      const filePromise = new Promise<FormImagePrompt | null | undefined>((resolve) => {
        baseHandleChangeFiles(idx, uploadFile, async (targetIdx: number) => {
          resolvedTargetIdx = targetIdx;
          const file = await handleChangeFiles(targetIdx, uploadFile, uploadFileInfo);
          resolve(file);
        });
      });
      const file = await filePromise;
      if (!file) return;

      //特殊处理强制裁剪强制裁剪
      if (forceCropRatio && file?.prompt) {
        const imagePrompt = file?.prompt;
        if (imagePrompt) {
          // 确保图片有尺寸信息
          const hasSize = await ensureImageSize(imagePrompt);
          if (hasSize && !checkImageAspectRatio(imagePrompt)) {
            // 宽高比不符合要求，打开裁剪框
            setForceCropIndex(resolvedTargetIdx);
            setForceCropOriginalData(file); // 保存原始上传数据
            forceCropReasonRef.current = 'upload';
            handleOpenSelectModal(resolvedTargetIdx, imagePrompt);
            return; // 阻止直接更新图片
          }
        }
      }
      updateImagePrompt(resolvedTargetIdx, file);
    };

    const handleConfirmSelectSample = async (uploaderIndex: number, imageUrl: string) => {
      const tempPrompt: FormImagePrompt = {
        id: uuidv4(),
        status: 'done',
        prompt: {
          sourceImage: imageUrl,
          sourceImagePreview: imageUrl,
          selectedImage: imageUrl,
          selectedImagePreview: imageUrl,
        },
      };
      updateImagePrompt(uploaderIndex, tempPrompt);
    };

    const renderUploader = (index: number) => {
      const operations = canCrop
        ? [
            {
              key: 'select',
              icon: <BaseLineCut />,
              text: t('create.createForm.upload.selectObject'),
              onClick: () => handleOpenSelectModal(index, imagePrompts[index]?.prompt ?? undefined),
              disabled: !imagePrompts[index]?.prompt,
            },
          ]
        : undefined;

      return (
        <SampleSelectHoverCard
          key={`img-${index}-${imagePrompts[index]?.prompt?.sourceImagePreview}`}
          samples={imageSamples?.[index] ?? []}
          onSelect={(imageUrl) => handleConfirmSelectSample(index, imageUrl)}
        >
          <div className="flex h-full w-[72px] flex-col items-center gap-3" id={`image-uploader-${index}`}>
            <ImageUploaderWithPreview
              className="flex h-full w-full items-center justify-center border border-system-white16 bg-[rgba(34,37,42,0.64)] backdrop-blur-2xl"
              uploaderClassName="hover:bg-system-white16 backdrop-blur-2xl"
              image={imagePrompts[index]?.prompt ?? null}
              onChange={(file, fileUploadInfo) => internalHandleChangeFiles(index, file, fileUploadInfo)}
              showHelperText={Boolean(imageUploadHints?.[index])}
              placeholder={imageUploadHints?.[index]}
              getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
              operations={operations}
              operationIconClassName="text-2xl"
              closeIconClassName="text-base"
              loading={
                processingImageIdx === index ||
                uploadingImages.includes(index) ||
                imagePrompts[index]?.status === 'uploading'
              }
              isInitialSelectedImageRange={canCrop}
              maxCount={emptySlotsCount || 1}
              idx={index}
              draggable={imageUploadCount > 1}
              onItemsReorder={handleSwapImages}
              allowTempMode={allowTempMode}
              ref={imagePrompts[index]?.id === flyEndId ? ref : undefined}
              imageContainerClassName={imagePrompts[index]?.id === flyEndId ? 'animate-fade-in opacity-0' : undefined}
            />
          </div>
        </SampleSelectHoverCard>
      );
    };

    if (!imageUploadCount) return null;

    return (
      <>
        <MediaDropZone
          className="contents"
          maxImageCount={emptySlotsCount}
          disabled={emptySlotsCount === 0 || isTemplateDetailDrawerOpen}
          getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
          allowTempMode={allowTempMode}
          acceptVideo={false}
          enablePaste={true}
          isInitialSelectedImageRange={canCrop}
          onChange={(file, type, info) => {
            if (type !== 'image') return;
            const startIdx = firstEmptySlotIndex >= 0 ? firstEmptySlotIndex : 0;
            void internalHandleChangeFiles(startIdx, file as ImageUploadFile, info as ImageUploadFileInfo | undefined);
          }}
        >
          {Array.from({ length: imageUploadCount }, (_, index) => renderUploader(index))}
        </MediaDropZone>

        <TemplateImgCropModal
          onClose={() => {
            // 先记录当前索引与触发原因，避免后续重置造成丢失
            const currentIdx = forceCropIndex;
            const reason = forceCropReasonRef.current;

            handleCloseSelectModal();

            // 如果是上传触发的强制裁剪，且用户取消了裁剪，则清理占位的 uploading 状态
            if (reason === 'upload' && currentIdx !== null && imagePrompts[currentIdx]?.status === 'uploading') {
              updateImagePrompt(currentIdx, null);
            }

            setForceCropIndex(null); // 清除强制裁剪标记
            setForceCropOriginalData(null); // 清除原始数据

            if (reason === 'templateChange') {
              router.back();
            }
            forceCropReasonRef.current = null;
          }}
          onConfirm={async (range) => {
            // 确认裁剪后不需要回退
            forceCropReasonRef.current = null;
            await handleSaveSelectImage(range);
          }}
          imageSource={selectModalImageSource}
          cropOptions={cropOptions}
          title={t('imgCropModal.title')}
          description={t('imgCropModal.description')}
          defaultCropIndex={defaultCropIndex}
        />
      </>
    );
  },
);
